import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import {
	CircleChevronLeft,
	Save,
	FileText,
	ListOrdered,
	BadgeQuestionMark,
} from 'lucide-react';
import { Input, InputError, InputLabel, InputWrapper } from '@/components';
import {
	TextArea,
	TextAreaError,
	TextAreaLabel,
	TextAreaWrapper,
} from '@/components';

const questionSchema = z.object({
	question: z
		.string()
		.min(1, 'Pergunta é obrigatória')
		.min(10, 'Pergunta deve ter no mínimo 10 caracteres')
		.max(500, 'Pergunta deve ter no máximo 500 caracteres'),
	answer: z
		.string()
		.min(1, 'Resposta é obrigatória')
		.min(10, 'Resposta deve ter no mínimo 10 caracteres')
		.max(2000, 'Resposta deve ter no máximo 2000 caracteres'),
	priority: z
		.number()
		.min(1, 'Prioridade deve ser no mínimo 1')
		.max(10, 'Prioridade deve ser no máximo 10'),
});

interface FaqFormProps {
	onSubmit: (data: Questions.Create | Questions.Question) => void;
	onBack: () => void;
	defaultValues?: Partial<Questions.Question>;
	isSubmitting?: boolean;
	formTitle?: string;
	submitButtonText?: string;
}

const FaqForm: React.FC<FaqFormProps> = ({
	onSubmit,
	defaultValues,
	isSubmitting: externalIsSubmitting = false,
	formTitle,
	submitButtonText = 'Salvar FAQ',
	onBack,
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting: internalIsSubmitting },
	} = useForm<Questions.Create | Questions.Question>({
		resolver: zodResolver(questionSchema),
		defaultValues,
	});

	const isSubmitting = externalIsSubmitting || internalIsSubmitting;

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="max-w-3xl bg-inherit md:min-w-96"
		>
			<div className="flex w-full flex-wrap items-center justify-between">
				<h2 className="text-lg font-bold text-dark">{formTitle}</h2>
			</div>

			<div className="space-y-6">
				<div className="space-y-1">
					<TextAreaLabel
						text="Pergunta"
						icon={
							<BadgeQuestionMark className="h-4 w-4 text-dark" />
						}
					/>
					<TextAreaWrapper>
						<TextArea
							placeholder="Digite a pergunta"
							rows={3}
							{...register('question')}
						/>
					</TextAreaWrapper>
					{errors.question?.message && (
						<TextAreaError error={errors.question.message} />
					)}
				</div>

				<div className="space-y-1">
					<TextAreaLabel
						text="Resposta"
						icon={<FileText className="h-4 w-4 text-dark" />}
					/>
					<TextAreaWrapper>
						<TextArea
							placeholder="Digite a resposta"
							rows={6}
							{...register('answer')}
						/>
					</TextAreaWrapper>
					{errors.answer?.message && (
						<TextAreaError error={errors.answer.message} />
					)}
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="space-y-1">
						<InputLabel
							text="Prioridade (1-10)"
							icon={<ListOrdered className="h-4 w-4 text-dark" />}
						/>
						<InputWrapper>
							<Input
								type="number"
								min={1}
								max={10}
								placeholder="Defina a prioridade (1-10)"
								{...register('priority', {
									valueAsNumber: true,
								})}
							/>
						</InputWrapper>
						{errors.priority?.message && (
							<InputError error={errors.priority.message} />
						)}
					</div>
				</div>
			</div>
			<div className="mt-6 flex justify-between">
				<Button
					onClick={onBack}
					text="Voltar"
					type="button"
					variant="outline"
					startIcon={<CircleChevronLeft className="w-4 text-dark" />}
				/>
				<Button
					type="submit"
					disabled={isSubmitting}
					endIcon={<Save className="w-4 text-light" />}
					text={submitButtonText}
				/>
			</div>
		</form>
	);
};

export { FaqForm };
